import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { Pie, Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, LinearScale, BarElement, CategoryScale, Title, PointElement, LineElement, LineController } from 'chart.js';
import { getUser } from '../../../reducers'
import LoadingPage from '../../components/LoadingPage'
import { firestore, db } from '../../../config/Fire'
import * as Constants from '../../../constants'
import { Col, Grid, Row } from 'react-flexbox-grid'
import { DatePicker, Button, message, Tabs, Select } from 'antd'
import './style.css'
import moment from 'moment'

ChartJS.register(
    LinearScale,
    ArcElement,
    Tooltip,
    Legend,
    Title,
    BarElement,
    CategoryScale,
    PointElement,
    LineElement,
    LineController
);

const { TabPane } = Tabs;
const { Option } = Select;

// Mover la función parseDate fuera del componente
    const parseDate = (dateString) => {
        const parts = dateString.split('/');
        if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return new Date(`${year}-${month}-${day}`);
        }
        return new Date(dateString);
    };

    /**
 * Helper para verificar si un bid tiene works asociados (compatibilidad con registros viejos)
 * @param {Object} bid - El bid a verificar
 * @param {Object} works - Objeto con todos los works
 * @returns {boolean} true si el bid tiene works asociados
 */
const hasWorksForBid = (bid, works) => {
    // Compatibilidad con registros viejos: verificar workOrderId
    if (bid.workOrderId) {
        return true
    }
    // Verificar si tiene works por bidId
    if (works && bid.id) {
        const worksForBid = Object.values(works).filter(work => work.bidId === bid.id)
        return worksForBid.length > 0
    }
    return false
}

/**
 * Calcula los porcentajes de elementos basados en un tipo específico
 * @param {Object} items - Objetos a analizar (bids o works)
 * @param {Date} startDate - Fecha de inicio del período
 * @param {Date} endDate - Fecha de fin del período
 * @param {string} type - Tipo de cálculo ('bids' o 'works')
 * @param {Object} works - Objeto con todos los works (necesario para verificar works por bidId)
 * @returns {Array} Array con dos porcentajes calculados [percentage1, percentage2]
 */
const calculatePercentages = (items, startDate, endDate, type, works = {}) => {
    let counter1 = 0;
    let counter2 = 0;
    const startDateTimestamp = new Date(startDate).getTime();
    const endDateTimestamp = new Date(endDate).getTime();

    Object.keys(items).forEach(itemId => {
        const item = items[itemId];
        const itemDate = new Date(item.startDate).getTime();
        if (itemDate >= startDateTimestamp && itemDate <= endDateTimestamp) {
            if (type === 'bids') {
                if (hasWorksForBid(item, works)) counter1 += 1;
                counter2 += 1;
            } else if (type === 'works') {
                if (item.company === 'Arcasa') counter1 += 1;
                if (item.company === 'SD') counter2 += 1;
            }
        }
    });

    const total = counter1 + counter2;
    const percentage1 = total > 0 ? (counter1 / total * 100).toFixed(2) : 0;
    const percentage2 = total > 0 ? (counter2 / total * 100).toFixed(2) : 0;

    return [percentage1, percentage2];
};

/**
 * Obtiene datos de Firestore según el tipo de colección y empresa
 * @param {string} collectionName - Nombre de la colección en Firestore
 * @param {string} resumeType - Tipo de empresa ('SD' o 'Arcasa')
 * @returns {Promise<Object>} Objeto con los datos cargados
 */
const fetchData = async (collectionName, resumeType) => {
    const docs = await firestore.collection(collectionName)
        .where('company', '==', resumeType)
        .get();
    const dataLoaded = {};
    if (!docs.empty) {
        docs.forEach(doc => {
            const data = doc.data();
            dataLoaded[data.id] = data;
        });
    }
    return dataLoaded;
};

/**
 * Renderiza los datos de clientes en formato de tabla
 * @param {Object} clientData - Datos de los clientes a mostrar
 * @returns {JSX.Element} Fragmento de React con filas de tabla
 */
const renderClientData = (clientData) => (
    <React.Fragment>
        {Object.keys(clientData).map((clientId, index) => (
            <tr key={clientId} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                <td style={{ border: "1px solid #EEE", padding: "6px", textAlign: "left" }}>{clientData[clientId].name}</td>
                <td style={{ border: "1px solid #EEE", padding: "6px", textAlign: "right" }}><strong>{clientData[clientId].count}</strong></td>
                <td style={{ border: "1px solid #EEE", padding: "6px", textAlign: "right" }}><strong>{clientData[clientId].lastDate}</strong></td>
            </tr>
        ))}
    </React.Fragment>
);

/**
 * Genera filas de tabla para el conteo de ciudades
 * @param {Object} cityCounts - Objeto con conteo de ciudades
 * @returns {Array<JSX.Element>} Array de elementos TR para tabla
 */
const generateTableRows = (cityCounts) => {
    return Object.entries(cityCounts).map(([city, count], index) => (
        <tr key={city} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "left" }}>{city}</td>
            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{count}</strong></td>
        </tr>
    ));
};

/**
 * Renderiza los datos de seguimiento de trabajos
 * @param {Object} works - Objeto con los trabajos a mostrar
 * @param {Object} bids - Objeto con las cotizaciones relacionadas
 * @param {Array} trips - Array de viajes realizados
 * @returns {Array<JSX.Element>} Array de elementos TR para la tabla de seguimiento
 */
const renderTrackingData = (works, bids, trips) => {
    return Object.values(works).map((work, index) => {
        const bid = bids[work.bidId];
        const workOrderLabel = work !== undefined ? getOTLabel(work) : 'N/A';
        const workTrips = work.trips;
        let totalKilometers = 0;
        
        // Calcular kilómetros totales
        if (workTrips) {
            workTrips.forEach(tripId => {
                const trip = trips.find(trip => trip.id === tripId);
                if (trip) {
                    const kilometers = Number(trip.kilometers) || 0;
                    totalKilometers += kilometers;
                }
            });
        }
        
        const totalTrips = workTrips !== undefined ? workTrips.length : 0;
        const startDate = work.startDate && work.startDate !== "" ? new Date(work.startDate) : null;
        const effectiveEndDate = work.effectiveEndDate && work.effectiveEndDate !== ""  ? new Date(work.effectiveEndDate) : null;
        let diffDays = 0;
        
        // Calcular días totales del trabajo
        if (startDate && effectiveEndDate) {
            const diffTime = Math.abs(effectiveEndDate - startDate);
            diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }

        return (
            <tr key={work.id} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{workOrderLabel}</td>
                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{totalKilometers} km</td>
                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{totalKilometers !== 0 ? totalKilometers / totalTrips : 0} km</td>
                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{work?.engineeringDays ?? 0} días</td>
                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{bid?.quotedEngineeringDays ?? 0} días</td>
                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{work?.invoicedPercentaje ?? 0}%</td>
                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{!isNaN(diffDays) ? diffDays : 0} días</td>
            </tr>
        );
    });
}

/**
 * Genera una etiqueta formateada para la orden de trabajo
 * @param {Object} prop - Objeto con los datos de la orden de trabajo
 * @returns {string} Etiqueta formateada de la OT
 */
const getOTLabel = (prop) => {
    const company = prop?.company === 'SD' ? 'SYD' : prop?.company === 'Arcasa' ? 'ARC' : 'O/T'
    if (prop.SAT) {
        return `${company} Nº${prop['workOrderNumber']}/${prop['year'].toString().substring(2, 4)} (SAT)`
    } else { 
        return `${company} Nº${prop['workOrderNumber']}/${prop['year'].toString().substring(2, 4)}`
    }
}

/**
 * Calcula los datos para los gráficos basados en trabajos y cotizaciones
 * @param {Object} works - Objeto con los trabajos
 * @param {Object} bids - Objeto con las cotizaciones
 * @param {Date} startDate - Fecha de inicio del período
 * @param {Date} endDate - Fecha de fin del período
 * @param {string|null} type - Tipo de categoría para filtrar
 * @returns {Array} Array con porcentajes [percentageOTs, percentageInvoiced]
 */
const calculateChartData = (works, bids, startDate, endDate, type) => {
    let totalBids = 0;
    // Contabilización: 1 OT por cotización (cotizaciones contratadas)
    let totalOTs = 0;
    // Facturación/otros: seguir considerando TODAS las OT del período
    let totalInvoicedPercentage = 0;
    let totalWorksWithInvoiced = 0;
    const startDateTimestamp = new Date(startDate).getTime();
    const endDateTimestamp = new Date(endDate).getTime();

    if (type === null) {
        // Cálculo sin filtro de categoría
        Object.values(bids).forEach(bid => {
            const bidDate = parseDate(bid.startDate).getTime();
            if (bidDate >= startDateTimestamp && bidDate <= endDateTimestamp) {
                totalBids += 1;
                if (hasWorksForBid(bid, works)) {
                    totalOTs += 1;
                }
            }
        });
        
        Object.values(works).forEach(work => {
            const workDate = parseDate(work.startDate).getTime();
            if (workDate >= startDateTimestamp && workDate <= endDateTimestamp) {
                if (work.invoicedPercentaje !== undefined) {
                    totalInvoicedPercentage += work.invoicedPercentaje;
                    totalWorksWithInvoiced += 1;
                }
            }
        });
    } else {
        // Cálculo con filtro de categoría
        const filteredBids = Object.values(bids).filter(bid => {
            const bidDate = parseDate(bid.startDate).getTime();
            return bidDate >= startDateTimestamp && bidDate <= endDateTimestamp && bid.category === type;
        });
        
        filteredBids.forEach(bid => {
            totalBids += 1;
                if (hasWorksForBid(bid, works)) {
                    totalOTs += 1;
                }
        });
        const filteredWorks = Object.values(works).filter(work => {
            const workDate = parseDate(work.startDate).getTime();
            return workDate >= startDateTimestamp && workDate <= endDateTimestamp && work.areas.includes(type);
        });
        
        totalInvoicedPercentage = filteredWorks.reduce((sum, work) => {
            const invoiced = work.invoicedPercentaje !== undefined ? work.invoicedPercentaje : 0
            return sum + invoiced
        }, 0);
        totalWorksWithInvoiced = filteredWorks.filter(work => work.invoicedPercentaje !== undefined).length
    }

    const percentageOTs = totalBids > 0 ? (totalOTs / totalBids * 100).toFixed(2) : 0;
    // Importante: para facturación/avance usamos todas las OT (no 1 por cotización)
    const percentageInvoiced = totalWorksWithInvoiced > 0 ? (totalInvoicedPercentage / totalWorksWithInvoiced).toFixed(2) : 0;

    return [percentageOTs, percentageInvoiced];
};

/**
 * Calcula los datos de facturación agrupados por tipo (empresa/monotributo)
 * @param {Object} bids - Objeto con las cotizaciones a analizar
 * @returns {Object} Objeto con los datos de facturación calculados
 */
const calculateBillingData = (bids, startDate, endDate) => {
    const billingData = {
        company: { amount: 0, count: 0 },
        monotributo: { amount: 0, count: 0 }
    };

    const startDateTimestamp = startDate ? parseDate(moment(startDate).format('DD/MM/YYYY')).getTime() : parseDate(moment().startOf('year').format('DD/MM/YYYY')).getTime();
    const endDateTimestamp = endDate ? parseDate(moment(endDate).format('DD/MM/YYYY')).getTime() : parseDate(moment().format('DD/MM/YYYY')).getTime();

    Object.values(bids).forEach(bid => {
        const bidDate = parseDate(bid.startDate).getTime();
        if (bidDate >= startDateTimestamp && bidDate <= endDateTimestamp && bid.billing && bid.amountDollars) {
            const billing = bid.billing === 'Monotributo' ? 'monotributo' : 'company';
            billingData[billing].amount += parseFloat(bid.amountDollars);
            billingData[billing].count += 1;
        }
    });
    
    const totalAmount = billingData.company.amount + billingData.monotributo.amount;
    billingData.company.incidencia = totalAmount > 0 ? (billingData.company.amount / totalAmount * 100).toFixed(2) : 0;
    billingData.monotributo.incidencia = totalAmount > 0 ? (billingData.monotributo.amount / totalAmount * 100).toFixed(2) : 0;
    return billingData;
};

const WorksEmployeeResume = ({ resumeType, user }) => {
    const [works, setWorks] = useState({})
    const [bids, setBids] = useState({})
    const [trips, setTrips] = useState({})
    const [clients, setClients] = useState()
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(moment().startOf('year'));
    const [endDate, setEndDate] = useState(moment());
    const [monotributoCount, setMonotributoCount] = useState(0);
    const [empresaCount, setEmpresaCount] = useState(0);
    const [clientData, setClientData] = useState({});
    const [categoryPercentages, setCategoryPercentages] = useState({});
    const [cityCounts, setCityCounts] = useState({});
    const [filteredWorks, setFilteredWorks] = useState([]);
    const [selectedClientType, setSelectedClientType] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [chartData, setChartData] = useState({ percentageOTs: 0, percentageInvoiced: 0 });
    const [selectedCategoryForChart, setSelectedCategoryForChart] = useState(null);
    const [timePeriod, setTimePeriod] = useState('anual');
    const [selectedYearForVEOfBids, setSelectedYearForVEOfBids] = useState(null);
    const [barChartData, setBarChartData] = useState({ labels: [], datasets: [] });
    const [billingData, setBillingData] = useState({ company: { amount: 0, count: 0 }, monotributo: { amount: 0, count: 0 } });
    const [billingChartData, setBillingChartData] = useState({});
    const [billingDataWithWorkOrderId, setBillingDataWithWorkOrderId] = useState({ company: { amount: 0, count: 0 }, monotributo: { amount: 0, count: 0 } });
    const [billingChartDataWithWorkOrderId, setBillingChartDataWithWorkOrderId] = useState({});
    const [privateClientCount, setPrivateClientCount] = useState(0);
    const [publicClientCount, setPublicClientCount] = useState(0);
    const [clientDistributionChartData, setClientDistributionChartData] = useState({});
    const [categoryBidsCounts, setCategoryBidsCounts] = useState({});
    const [categoryBidsPercentages, setCategoryBidsPercentages] = useState({});
    const [categoryBidsChartData, setCategoryBidsChartData] = useState({});
    const [annualBillingChartData, setAnnualBillingChartData] = useState({});
    const [categoryBillingData, setCategoryBillingData] = useState({});
    const [categoryBillingChartData, setCategoryBillingChartData] = useState({});
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [annualContractedBillingChartData, setAnnualContractedBillingChartData] = useState({});
    const [selectedYearForOTAnual ,setSelectedYearForOTAnual ] = useState(new Date().getFullYear());
    const [categoryContractedBillingData, setCategoryContractedBillingData ] = useState({});
    const [categoryContractedBillingChartData, setCategoryContractedBillingChartData ] = useState({});
    const [totalContractedAmount, setTotalContractedAmount] = useState(0);
    const [totalOTs, setTotalOTs] = useState(0);
    const [barChartEficientlyData , setBarChartEficientlyData] = useState({});
    const [barChartEficientlyOptions , setBarChartEficientlyOptions] = useState([]);

    const invoicedData = {
        labels: ['Contrataciones', 'Facturado'],
        datasets: [
            {
                data: [chartData.percentageOTs, chartData.percentageInvoiced],
                backgroundColor: [
                    'rgba(54, 162, 235, 0.6)',
                    'rgba(75, 192, 192, 0.3)'
                ],
                borderColor: [
                    'rgba(54, 162, 235, 1)',
                    'rgba(75, 192, 192, 1)'
                ],
                borderWidth: 1,
            },
        ],
    };
    const [data, setData] = useState({
        labels: ['Cantidad de cotizaciones', 'OT dentro de cotizaciones'],
        datasets: [
            {
                data: [12, 19],
                backgroundColor: [
                'rgba(255, 99, 132, 0.6)',
                'rgba(54, 162, 235, 0.6)',
                ],
                borderColor: [
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)',
                ],
                borderWidth: 1,
            },
        ],
    });
    const options = {
        plugins: {
            title: {
                display: true,
                text: 'Resumen de cotizaciones',
                font: {
                    size: 18,
                },
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                color: '#000',
                font: {
                    size: 14,
                },
                },
            },
            tooltip: {
                backgroundColor: '#f5f5f5',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#ddd',
                borderWidth: 1,
                borderCapStyle: 'round',
                callbacks: {
                    label: function(tooltipItem) {
                        const dataset = tooltipItem.dataset;
                        const currentValue = dataset.data[tooltipItem.dataIndex];
                        return `${currentValue}%`;
                    }
                }
            },
        },
    };
    const options1 = {
        plugins: {
            title: {
                display: true,
                text: 'Porcentaje de Contratación y Facturación',
                font: {
                    size: 18,
                },
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    color: '#000',
                    font: {
                        size: 14,
                    },
                },
            },
            tooltip: {
                backgroundColor: '#f5f5f5',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#ddd',
                borderWidth: 1,
                borderCapStyle: 'round',
                callbacks: {
                    label: function(tooltipItem) {
                        const dataset = tooltipItem.dataset;
                        const currentValue = dataset.data[tooltipItem.dataIndex];
                        return `${currentValue}%`;
                    }
                }
            },
        },
    };

    /**
     * Efecto para cargar los datos iniciales del componente
     * Realiza la carga de trabajos, cotizaciones, clientes y viajes desde Firestore
     */
    useEffect(() => {
        async function getData() {
            setLoading(true);
            try {
                // Cargar datos en paralelo
                const [worksLoaded, bidsLoaded, clientsResponse, tripsResponse] = await Promise.all([
                    fetchData(Constants.MAIN_NODES.WORK_ORDERS, resumeType),
                    fetchData(Constants.MAIN_NODES.BID_ORDERS, resumeType), 
                    db.ref('users').orderByChild('userType').equalTo('CLIENT').once('value'),
                    firestore.collection('trips').get()
                ]);

                // Establecer datos base
                const clientsLoaded = clientsResponse.val();
                const tripsLoaded = tripsResponse.docs.map(doc => doc.data());
                
                setWorks(worksLoaded);
                setBids(bidsLoaded);
                setClients(clientsLoaded);
                setTrips(tripsLoaded);

                // Filtrar por fechas
                const startDateTimestamp = parseDate(moment(startDate).format('DD/MM/YYYY')).getTime();
                const endDateTimestamp = parseDate(moment(endDate).format('DD/MM/YYYY')).getTime();

                const filteredBids = Object.values(bidsLoaded).filter(bid => {
                    const bidDate = parseDate(bid.startDate).getTime();
                    return bidDate >= startDateTimestamp && bidDate <= endDateTimestamp;
                });

                const filteredWorks = Object.values(worksLoaded).filter(work => {
                    const workDate = parseDate(work.startDate).getTime();
                    return workDate >= startDateTimestamp && workDate <= endDateTimestamp;
                });
                setFilteredWorks(filteredWorks);

                // Calcular porcentajes y estadísticas iniciales
                const calculateYearlyPercentages = (data, type, currentYear = true) => {
                    const year = currentYear ? new Date().getFullYear() : new Date().getFullYear() - 1;
                    return calculatePercentages(data, new Date(year, 0, 1), new Date(), type, worksLoaded);
                };

                // Actualizar datos de gráficos y estadísticas
                let [workCounterPercentage, workOrderIdPercentage] = calculateYearlyPercentages(bidsLoaded, 'bids');
                if (workCounterPercentage === 0 && workOrderIdPercentage === 0) {
                    [workCounterPercentage, workOrderIdPercentage] = calculateYearlyPercentages(bidsLoaded, 'bids', false);
                }

                setData(prevData => ({
                    ...prevData,
                    datasets: [{
                        ...prevData.datasets[0],
                        data: [workCounterPercentage, workOrderIdPercentage],
                    }],
                }));

                // Calcular datos de gráficos
                let [percentageOTs, percentageInvoiced] = calculateChartData(worksLoaded, bidsLoaded, new Date(new Date().getFullYear(), 0, 1), new Date(), null);
                if (percentageOTs === 0 && percentageInvoiced === 0) {
                    [percentageOTs, percentageInvoiced] = calculateChartData(worksLoaded, bidsLoaded, new Date(new Date().getFullYear() - 1, 0, 1), new Date(), null);
                }
                setChartData({ percentageOTs, percentageInvoiced });

                // Procesar datos de licitaciones
                const billingTypes = filteredBids.reduce((acc, bid) => {
                    acc[bid.billing === 'Monotributo' ? 'mono' : 'emp']++;
                    return acc;
                }, { mono: 0, emp: 0 });

                setMonotributoCount(billingTypes.mono);
                setEmpresaCount(billingTypes.emp);

                // Calcular porcentajes y conteos
                setCategoryPercentages(calculateCategoryPercentages(filteredBids));
                
                const cityCounts = countContractsByCity(filteredBids);
                generateTableRows(cityCounts);
                setCityCounts(cityCounts);

                const billingResponse = calculateBillingData(filteredBids, startDate, endDate);
                setBillingData(billingResponse);
                setBillingDataWithWorkOrderId(calculateBillingData(
                    Object.values(filteredBids).filter(bid => hasWorksForBid(bid, works)),
                    startDate,
                    endDate
                ));

                // Conteo de clientes
                const clientCounts = filteredBids.reduce((acc, bid) => {
                    const bidClients = bid?.clients
                    if (bidClients && Array.isArray(bidClients)) {
                        bidClients.forEach(clientId => {
                            const isPrivate = clientsLoaded?.[clientId]?.type === true
                            acc[isPrivate ? 'private' : 'public']++
                        })
                    }
                    return acc;
                }, { private: 0, public: 0 });

                setPrivateClientCount(clientCounts.private);
                setPublicClientCount(clientCounts.public);

                // Calcular estadísticas de categorías
                const categoryStats = filteredBids.reduce((acc, bid) => {
                    if (bid.category) {
                        acc.counts[bid.category] = (acc.counts[bid.category] || 0) + 1;
                    }
                    return acc;
                }, { counts: {}, total: filteredBids.length });

                const categoryPercentages = Object.keys(categoryStats.counts).reduce((acc, category) => {
                    acc[category] = ((categoryStats.counts[category] / categoryStats.total) * 100).toFixed(2);
                    return acc;
                }, {});

                setCategoryBidsCounts(categoryStats.counts);
                setCategoryBidsPercentages(categoryPercentages);

                // Calcular totales finales
                const contractedBids = filteredBids.filter(bid => hasWorksForBid(bid, works));
                setTotalContractedAmount(contractedBids.reduce((sum, bid) => sum + parseFloat(bid.amountDollars || 0), 0));
                setTotalOTs(contractedBids.length);

                setLoading(false);
            } catch (err) {
                console.error('Error al obtener datos:', err);
                message.error('Error al cargar los datos');
                setLoading(false);
            }
        }
        getData();
    }, []);

    /**
     * Efecto para actualizar los datos del gráfico de categorías
     * Se ejecuta cuando cambian los porcentajes de cotizaciones por categoría
     */
    useEffect(() => {
        const categoryMap = resumeType === 'SD' ? Constants.categorySDBidOrderMap : Constants.categoryARCASABidOrderMap;
        
        const chartData = {
            labels: Object.keys(categoryMap),
            datasets: [
                {
                    data: Object.keys(categoryMap).map(key => categoryBidsPercentages[categoryMap[key]] || 0),
                    backgroundColor: [
                        'rgba(255, 99, 132, 0.6)',  // Rojo
                        'rgba(54, 162, 235, 0.6)',   // Azul
                        'rgba(255, 206, 86, 0.6)',   // Amarillo
                        'rgba(75, 192, 192, 0.6)',   // Verde
                        'rgba(153, 102, 255, 0.6)',  // Morado
                        'rgba(255, 159, 64, 0.6)',   // Naranja
                        'rgba(199, 199, 199, 0.6)',  // Gris
                        'rgba(255, 99, 71, 0.6)',    // Tomate
                        'rgba(144, 238, 144, 0.6)',  // Verde claro
                        'rgba(173, 216, 230, 0.6)',  // Azul claro
                        'rgba(255, 182, 193, 0.6)',  // Rosado claro
                        'rgba(255, 215, 0, 0.6)',    // Oro
                        'rgba(0, 128, 128, 0.6)',    // Teal
                        'rgba(255, 69, 0, 0.6)',     // Naranja oscuro
                        'rgba(128, 0, 128, 0.6)',    // Morado oscuro
                        'rgba(0, 255, 127, 0.6)',    // Verde claro
                        'rgba(70, 130, 180, 0.6)',   // Azul marino
                        'rgba(210, 105, 30, 0.6)',   // Chocolate
                        'rgba(255, 20, 147, 0.6)',   // Fucsia
                        'rgba(0, 191, 255, 0.6)',    // Azul cielo
                    ],
                    borderColor: [
                        'rgba(255, 99, 132, 1)',
                        'rgba(54, 162, 235, 1)',
                        'rgba(255, 206, 86, 1)',
                        'rgba(75, 192, 192, 1)',
                        'rgba(153, 102, 255, 1)',
                        'rgba(255, 159, 64, 1)',
                        'rgba(199, 199, 199, 1)',
                        'rgba(255, 99, 71, 1)',
                        'rgba(144, 238, 144, 1)',
                        'rgba(173, 216, 230, 1)',
                        'rgba(255, 182, 193, 1)',
                        'rgba(255, 215, 0, 1)',
                        'rgba(0, 128, 128, 1)',
                        'rgba(255, 69, 0, 1)',
                        'rgba(128, 0, 128, 1)',
                        'rgba(0, 255, 127, 1)',
                        'rgba(70, 130, 180, 1)',
                        'rgba(210, 105, 30, 1)',
                        'rgba(255, 20, 147, 1)',
                        'rgba(0, 191, 255, 1)',
                        'rgba(0, 191, 255, 1)',
                    ],
                    borderWidth: 1,
                },
            ],
        };
        setCategoryBidsChartData(chartData);
    }, [categoryBidsPercentages]);

    /**
     * Efecto para inicializar datos de clientes y facturación anual
     * Se ejecuta cuando cambian las cotizaciones
     */
    useEffect(() => {
        const initializeClientData = async (bids) => {
            const clientDataArr = await updateClientData(bids);
            setClientData(clientDataArr);
        };
        initializeClientData(bids);

        // Calcular datos de facturación anual
        const annualBillingData = {};
        const annualContractedBillingData = {};

        Object.values(bids).forEach(bid => {
            const year = bid.year;
            const amount = parseFloat(bid.amountDollars) || 0;

            if (!annualBillingData[year]) {
                annualBillingData[year] = 0;
            }
            annualBillingData[year] += amount;

                if (hasWorksForBid(bid, works)) {
                if (!annualContractedBillingData[year]) {
                    annualContractedBillingData[year] = 0;
                }
                annualContractedBillingData[year] += amount;
            }
        });

        // Crear datos para los gráficos
        const createChartData = (data, label) => {
            const labels = Object.keys(data).sort();
            const chartData = labels.map(year => data[year]);

            return {
                labels,
                datasets: [
                    {
                        label,
                        data: chartData,
                        backgroundColor: 'rgba(54, 162, 235, 0.6)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1,
                    },
                ],
            };
        };

        setAnnualBillingChartData(createChartData(annualBillingData, 'Monto cotizado (USD)'));
        setAnnualContractedBillingChartData(createChartData(annualContractedBillingData, 'Monto contratado (USD)'));
    }, [bids]);

    useEffect(() => {
        const categoryMap = resumeType === 'SD' ? Constants.categorySDBidOrderMap : Constants.categoryARCASABidOrderMap;
        const invertedCategoryMap = Object.fromEntries(Object.entries(categoryMap).map(([key, value]) => [value, key]));
        const categoryBillingData = {};
    
        // Inicializar categoryBillingData con todas las categorías
        Object.keys(categoryMap).forEach(key => {
            categoryBillingData[categoryMap[key]] = { amount: 0, count: 0, incidencia: 0 };
        });
    
        Object.values(bids).forEach(bid => {
            const year = parseDate(bid.startDate).getFullYear();
            const category = invertedCategoryMap[bid.category];
            if (year === selectedYear && bid.amountDollars && category) {
                if (!categoryBillingData[categoryMap[category]]) {
                    categoryBillingData[categoryMap[category]] = { amount: 0, count: 0, incidencia: 0 };
                }
                categoryBillingData[categoryMap[category]].amount += parseFloat(bid.amountDollars);
                categoryBillingData[categoryMap[category]].count += 1;
            }
        });
    
        const totalAmount = Object.values(categoryBillingData).reduce((sum, category) => sum + category.amount, 0);
        Object.keys(categoryBillingData).forEach(category => {
            categoryBillingData[category].incidencia = totalAmount > 0 ? (categoryBillingData[category].amount / totalAmount * 100).toFixed(2) : 0;
        });
    
        setCategoryBillingData(categoryBillingData);
    
        const labels = Object.keys(categoryMap).map(key => categoryMap[key]);
        const data = labels.map(label => categoryBillingData[label]?.amount || 0);
    
        const chartData = {
            labels,
            datasets: [
                {
                    label: 'Monto cotizado (USD)',
                    data,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
            ],
        };
        setCategoryBillingChartData(chartData);
    }, [bids, selectedYear, resumeType]);

    useEffect(() => {
        const categoryMap = resumeType === 'SD' ? Constants.categorySDBidOrderMap : Constants.categoryARCASABidOrderMap;
        const invertedCategoryMap = Object.fromEntries(Object.entries(categoryMap).map(([key, value]) => [value, key]));
        const contractedBillingData = {};
    
        // Inicializar categoryBillingData con todas las categorías
        Object.keys(categoryMap).forEach(key => {
            contractedBillingData[categoryMap[key]] = { amount: 0, count: 0, incidencia: 0 };
        });
    
        Object.values(bids).forEach(bid => {
            const year = parseDate(bid.startDate).getFullYear();
            const category = invertedCategoryMap[bid.category];
            // Contabilización de contratadas: 1 por cotización, incluso si tiene múltiples OT
            if (hasWorksForBid(bid, works) && year === selectedYearForOTAnual && bid.amountDollars && category) {
                if (!contractedBillingData[categoryMap[category]]) {
                    contractedBillingData[categoryMap[category]] = { amount: 0, count: 0, incidencia: 0 };
                }
                contractedBillingData[categoryMap[category]].amount += parseFloat(bid.amountDollars);
                contractedBillingData[categoryMap[category]].count += 1;
            }
        });
    
        const totalAmount = Object.values(contractedBillingData).reduce((sum, category) => sum + category.amount, 0);
        Object.keys(contractedBillingData).forEach(category => {
            contractedBillingData[category].incidencia = totalAmount > 0 ? (contractedBillingData[category].amount / totalAmount * 100).toFixed(2) : 0;
        });
    
        setCategoryContractedBillingData(contractedBillingData);
    
        const labels = Object.keys(categoryMap).map(key => categoryMap[key]);
        const data = labels.map(label => contractedBillingData[label]?.amount || 0);
    
        const chartData = {
            labels,
            datasets: [
                {
                    label: 'Monto contratado (USD)',
                    data,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
            ],
        };
        setCategoryContractedBillingChartData(chartData);
    }, [bids, selectedYearForOTAnual, resumeType, works]);

    /**
     * Efecto para actualizar los datos del gráfico de facturación
     * Se ejecuta cuando cambian los datos de facturación
     */
    useEffect(() => {
        const billingChartData = {
            labels: ['Empresa', 'Monotributo'],
            datasets: [
                {
                    data: [billingData.company.incidencia, billingData.monotributo.incidencia],
                    backgroundColor: ['rgba(54, 162, 235, 0.6)', 'rgba(255, 165, 0, 0.6)'],
                    borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 165, 0, 1)'],
                    borderWidth: 1,
                },
            ],
        };
        setBillingChartData(billingChartData);
    }, [billingData]);

    /**
     * Efecto para actualizar el gráfico de distribución de clientes
     * Se ejecuta cuando cambian los conteos de clientes privados y públicos
     */
    useEffect(() => {
        const totalClients = privateClientCount + publicClientCount;
        const privateClientPercentage = totalClients > 0 ? (privateClientCount / totalClients * 100).toFixed(2) : 0;
        const publicClientPercentage = totalClients > 0 ? (publicClientCount / totalClients * 100).toFixed(2) : 0;

        const chartData = {
            labels: ['Privado', 'Público'],
            datasets: [
                {
                    data: [privateClientPercentage, publicClientPercentage],
                    backgroundColor: ['rgba(54, 162, 235, 0.6)', 'rgba(255, 165, 0, 0.6)'],
                    borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 165, 0, 1)'],
                    borderWidth: 1,
                },
            ],
        };
        setClientDistributionChartData(chartData);
    }, [privateClientCount, publicClientCount]);

    /**
     * Efecto para actualizar el gráfico de facturación con órdenes de trabajo
     * Se ejecuta cuando cambian los datos de facturación con workOrderId
     */
    useEffect(() => {
        const billingChartDataWithWorkOrderId = {
            labels: ['Empresa', 'Monotributo'],
            datasets: [
                {
                    data: [billingDataWithWorkOrderId.company.incidencia, billingDataWithWorkOrderId.monotributo.incidencia],
                    backgroundColor: ['rgba(54, 162, 235, 0.6)', 'rgba(255, 165, 0, 0.6)'],
                    borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 165, 0, 1)'],
                    borderWidth: 1,
                },
            ],
        };
        setBillingChartDataWithWorkOrderId(billingChartDataWithWorkOrderId);
    }, [billingDataWithWorkOrderId]);

    /**
     * Efecto para actualizar los datos del gráfico de barras según el período seleccionado
     * Se ejecuta cuando cambia el período o el año seleccionado
     */
    useEffect(() => {
        if (timePeriod === 'anual') {
            calculateAnnualBarChartData();
        } else if (timePeriod === 'mensual' && selectedYearForVEOfBids) {
            calculateMonthlyBarChartData(selectedYearForVEOfBids);
        }
    }, [timePeriod, selectedYearForVEOfBids, bids]);

    /**
     * Efecto para actualizar los datos de eficiencia por categoría
     * Se ejecuta cuando cambian los conteos de categorías o las cotizaciones
     */
    useEffect(() => {
        const labels = Object.keys(categoryBidsCounts);
        const totalBidsData = labels.map(category => categoryBidsCounts[category]);
        const contractedBidsData = labels.map(category => {
            return Object.values(bids).filter(bid => {
                const bidDate = parseDate(bid.startDate).getTime();
                const startDateTimestamp = startDate ? new Date(startDate).getTime() : 0;
                const endDateTimestamp = endDate ? new Date(endDate).getTime() : Number.MAX_SAFE_INTEGER;
                
                return bid.category === category && 
                        hasWorksForBid(bid, works) && 
                        bidDate >= startDateTimestamp && 
                        bidDate <= endDateTimestamp;
            }).length;
        });
        const efficiencyData = labels.map((category, index) => {
            const totalBids = totalBidsData[index];
            const contractedBids = contractedBidsData[index];
            return totalBids > 0 ? ((contractedBids / totalBids) * 100).toFixed(2) : 0;
        });

        // Configuración del gráfico de barras con línea de eficiencia
        const barChartData = {
            labels,
            datasets: [
                {
                    label: 'Cotizaciones',
                    data: totalBidsData,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    label: 'Contratadas',
                    data: contractedBidsData,
                    backgroundColor: 'rgba(255, 165, 0, 0.6)',
                    borderColor: 'rgba(255, 165, 0, 1)',
                    borderWidth: 1,
                },
                {
                    label: 'Eficiencia',
                    data: efficiencyData,
                    type: 'line',
                    fill: false,
                    borderColor: 'rgba(0, 128, 0, 1)',
                    backgroundColor: 'rgba(0, 128, 0, 0.6)',
                    yAxisID: 'y-axis-2',
                },
            ],
        };

        // Opciones del gráfico
        const barChartOptions = {
            scales: {
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Cantidad',
                    },
                },
                'y-axis-2': {
                    beginAtZero: true,
                    position: 'right',
                    title: {
                        display: true,
                        text: 'Eficiencia (%)',
                    },
                    grid: {
                        drawOnChartArea: false,
                    },
                    ticks: {
                        callback: function(value) {
                            return value + '%';
                        }
                    }
                },
            },
            plugins: {
                legend: {
                    position: 'bottom',
                },
                tooltip: {
                    callbacks: {
                        label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const currentValue = dataset.data[tooltipItem.dataIndex];
                            return dataset.label === 'Eficiencia' ? `${currentValue}%` : currentValue;
                        },
                    },
                },
            },
        };
        
        setBarChartEficientlyData(barChartData);
        setBarChartEficientlyOptions(barChartOptions);
    }, [categoryBidsCounts, bids, startDate, endDate]); // Added startDate and endDate to dependencies

    /**
     * Obtiene los datos filtrados por fecha
     * @returns {Promise<void>} 
     */
    const getDataByDate = async () => {
        if (startDate && endDate) {
            // Calcular porcentajes de cotizaciones
            const [workCounterPercentage, workOrderIdPercentage] = calculatePercentages(
                bids, 
                startDate.toDate(), 
                endDate.toDate(), 
                'bids',
                works
            );
            setData(prevData => ({
                ...prevData,
                datasets: [{
                    ...prevData.datasets[0],
                    data: [workCounterPercentage, workOrderIdPercentage],
                }],
            }));

            // Contar órdenes por tipo de facturación
            let monoCount = 0;
            let empCount = 0;
            const startDateTimestamp = parseDate(moment(startDate).format('DD/MM/YYYY')).getTime();
            const endDateTimestamp = parseDate(moment(endDate).format('DD/MM/YYYY')).getTime();
            const filteredBids = {};
            
            Object.values(bids).forEach(bid => {
                const bidDate = parseDate(bid.startDate).getTime();
                if (bidDate >= startDateTimestamp && bidDate <= endDateTimestamp) {
                    filteredBids[bid.id] = bid;
                    if (bid.billing === 'Monotributo') monoCount += 1;
                    if (bid.billing === 'Empresa') empCount += 1;
                }
            });

            setMonotributoCount(monoCount);
            setEmpresaCount(empCount);

            // Actualizar conteos de clientes
            const privateCount = Object.values(filteredBids)
                .filter(bid => bid.clients.some(clientId => clients[clientId]?.type === true)).length;
            const publicCount = Object.values(filteredBids)
                .filter(bid => bid.clients.some(clientId => clients[clientId]?.type === false)).length;

            setPrivateClientCount(privateCount);
            setPublicClientCount(publicCount);

            // Actualizar datos de facturación
            const billingResponse = calculateBillingData(filteredBids, startDate, endDate);
            setBillingData(billingResponse);
            setBillingDataWithWorkOrderId(calculateBillingData(
                Object.values(filteredBids).filter(bid => hasWorksForBid(bid, works)),
                startDate,
                endDate
            ));
            
            // Actualizar datos de clientes y categorías
            const clientDataArr = await updateClientData(filteredBids);
            setClientData(clientDataArr);
            setCategoryPercentages(calculateCategoryPercentages(filteredBids));
            
            // Actualizar conteos por ciudad
            const cityCounts = countContractsByCity(filteredBids);
            generateTableRows(cityCounts);
            setCityCounts(cityCounts);

            // Calcular estadísticas por categoría
            const counts = {};
            const totalBids = Object.values(filteredBids).length;
            Object.values(filteredBids).forEach(bid => {
                if (bid.category) {
                    counts[bid.category] = (counts[bid.category] || 0) + 1;
                }
            });

            const percentages = {};
            Object.keys(counts).forEach(category => {
                percentages[category] = ((counts[category] / totalBids) * 100).toFixed(2);
            });
            
            setCategoryBidsCounts(counts);
            setCategoryBidsPercentages(percentages);

            // Filtrar trabajos por fecha
            const filtered = {};
            Object.values(works).forEach(work => {
                const workDate = parseDate(work.startDate).getTime();
                if (workDate >= startDateTimestamp && workDate <= endDateTimestamp) {
                    filtered[work.id] = work;
                }
            });
            setFilteredWorks(filtered);

            // Actualizar datos de gráficos
            const [percentageOTs, percentageInvoiced] = calculateChartData(
                works, 
                bids, 
                startDate.toDate(), 
                endDate.toDate(), 
                null
            );
            setChartData({ percentageOTs, percentageInvoiced });

            // Calcular totales finales
            const contractedBids = Object.values(filteredBids).filter(bid => hasWorksForBid(bid, works));
            setTotalContractedAmount(contractedBids.reduce((sum, bid) => sum + parseFloat(bid.amountDollars || 0), 0));
            setTotalOTs(contractedBids.length);
        } else {
            message.error('Debe seleccionar una fecha de inicio y una fecha de fin');
            window.location.reload();
        }
    };

    /**
     * Calcula los porcentajes por categoría de las cotizaciones
     * @param {Object} filteredBids - Cotizaciones filtradas a analizar
     * @returns {Object} Objeto con los porcentajes por categoría
     */
    const calculateCategoryPercentages = (filteredBids) => {
        const categoryCounts = {};
        const validBids = filteredBids && Object.keys(filteredBids).length > 0 ? filteredBids : bids;
        const totalBids = Object.keys(validBids).length;

        Object.values(validBids).forEach(bid => {
            const category = bid.category;
            if (category) {
                if (!categoryCounts[category]) {
                    categoryCounts[category] = 0;
                }
                categoryCounts[category] += 1;
            }
        });

        const categoryPercentages = {};
        Object.keys(categoryCounts).forEach(category => {
            categoryPercentages[category] = ((categoryCounts[category] / totalBids) * 100).toFixed(2);
        });
        return categoryPercentages;
    };

    /**
     * Calcula los datos del gráfico de barras para visualización anual
     */
    const calculateAnnualBarChartData = () => {
        const years = [...new Set(Object.values(bids).map(bid => bid.year))];
        const labels = years.sort();
        const bidCounts = labels.map(year => Object.values(bids).filter(bid => {
            const bidDate = parseDate(bid.startDate);
            return bidDate.getFullYear() === year;
        }).length);
        const contractedCounts = labels.map(year => Object.values(bids).filter(bid => {
            const bidDate = parseDate(bid.startDate);
            return bidDate.getFullYear() === year && hasWorksForBid(bid, works)
        }).length);

        setBarChartData({
            labels,
            datasets: [
                {
                    label: 'Cantidad cotizaciones realizadas',
                    data: bidCounts,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    label: 'Cantidad cotizaciones contratadas',
                    data: contractedCounts,
                    backgroundColor: 'rgba(255, 165, 0, 0.6)',
                    borderColor: 'rgba(255, 165, 0, 1)',
                    borderWidth: 1,
                },
            ],
        });
    };

    /**
     * Calcula los datos del gráfico de barras para visualización mensual
     * @param {number} year - Año seleccionado para el cálculo
     */
    const calculateMonthlyBarChartData = (year) => {
        const months = Array.from({ length: 12 }, (_, i) => i);
        const labels = months.map(month => new Date(0, month).toLocaleString('default', { month: 'long' }));
        const bidCounts = months.map(month => Object.values(bids).filter(bid => {
            const bidDate = parseDate(bid.startDate);
            return bidDate.getFullYear() === year && bidDate.getMonth() === month;
        }).length);
        const contractedCounts = months.map(month => Object.values(bids).filter(bid => {
            const bidDate = parseDate(bid.startDate);
            return bidDate.getFullYear() === year && bidDate.getMonth() === month && hasWorksForBid(bid, works);
        }).length);

        setBarChartData({
            labels,
            datasets: [
                {
                    label: 'Cantidad cotizaciones realizadas',
                    data: bidCounts,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    label: 'Cantidad cotizaciones contratadas',
                    data: contractedCounts,
                    backgroundColor: 'rgba(255, 165, 0, 0.6)',
                    borderColor: 'rgba(255, 165, 0, 1)',
                    borderWidth: 1,
                },
            ],
        });
    };

    let allClients = null;

    /**
     * Obtiene todos los clientes de la base de datos
     * @returns {Promise<Object|null>} Objeto con los datos de los clientes o null
     */
    const fetchAllClients = async () => {
        if (!allClients) {
            const clientSnapshot = await db.ref('users').orderByChild('userType').equalTo('CLIENT').once('value');
            allClients = clientSnapshot.val();
        }
        return allClients;
    };

    /**
     * Obtiene el nombre de un cliente específico
     * @param {string} clientId - ID del cliente a buscar
     * @returns {Promise<string|null>} Nombre del cliente o null si no se encuentra
     */
    const fetchClientName = async (clientId) => {
        const clients = await fetchAllClients();
        for (const key in clients) {
            if (clients[key].id === clientId) {
                return clients[key].name;
            }
        }
        return null;
    };
    
    /**
     * Actualiza los datos de clientes basado en las cotizaciones
     * @param {Object} filteredBids - Cotizaciones filtradas a analizar
     * @returns {Promise<Object>} Objeto con los datos actualizados de los clientes
     */
    const updateClientData = async (filteredBids) => {
        const validBids = filteredBids && Object.keys(filteredBids).length > 0 
            ? filteredBids 
            : (typeof bids === 'object' && bids !== null ? bids : {});
        const clientData = {};

        for (const order of Object.values(validBids)) {
            if (!order.clients) continue;
            for (const client of order.clients) {
                if (!clientData[client]) {
                    const clientName = await fetchClientName(client);
                    clientData[client] = { name: clientName, count: 0, lastDate: null };
                }
                clientData[client].count += 1;
                if (!clientData[client].lastDate || new Date(order.startDate) > new Date(clientData[client].lastDate)) {
                    clientData[client].lastDate = order.startDate;
                }
            }
        }
        return clientData;
    };

    /**
     * Cuenta las contrataciones por ciudad
     * @param {Object} bids - Cotizaciones a analizar
     * @returns {Object} Objeto con el conteo de contrataciones por ciudad
     */
    const countContractsByCity = (bids) => {
        const cityCounts = {};
        Object.values(bids).forEach(bid => {
            const city = bid.city;
            if (city) {
                cityCounts[city] = (cityCounts[city] || 0) + 1;
            }
        });
        return cityCounts;
    };

    /**
     * Maneja el cambio de categoría en el filtro
     * @param {string} value - Valor de la categoría seleccionada
     */
    const handleCategoryChange = (value) => {
        if (value !== undefined) {
            setSelectedClientType(null);
            setSelectedCategory(value);
            let filteredByDate = {};
            const startDateTimestamp = parseDate(moment(startDate).format('DD/MM/YYYY')).getTime();
            const endDateTimestamp = parseDate(moment(endDate).format('DD/MM/YYYY')).getTime();
        
            // Filtrar por fecha si hay rango seleccionado
            if (startDate && endDate) {
                Object.values(works).forEach(work => {
                    const workDate = parseDate(work.startDate).getTime();
                    if (workDate >= startDateTimestamp && workDate <= endDateTimestamp) {
                        filteredByDate[work.id] = work;
                    }
                });
            } else filteredByDate = works;
        
            // Filtrar por categoría
            const filteredByCategory = {};
            Object.values(filteredByDate).forEach(work => {
                const bid = bids[work.bidId];
                if (bid && bid.category === value) {
                    filteredByCategory[work.id] = work;
                }
            });
        
            setFilteredWorks(filteredByCategory);
        }
    };

    /**
     * Maneja el cambio de tipo de cliente en el filtro
     * @param {string} value - Valor del tipo de cliente seleccionado
     */
    const handleClientTypeChange = (value) => {
        if (value !== undefined) {
            setSelectedClientType(value);
            setSelectedCategory(null);
            const type = value === 'privado' ? true : false;
            const filteredByClientType = {};
            let filteredByDate = {};
            const startDateTimestamp = parseDate(moment(startDate).format('DD/MM/YYYY')).getTime();
            const endDateTimestamp = parseDate(moment(endDate).format('DD/MM/YYYY')).getTime();
        
            // Filtrar por fecha si hay rango seleccionado
            if (startDate && endDate) {
                Object.values(works).forEach(work => {
                    const workDate = parseDate(work.startDate).getTime();
                    if (workDate >= startDateTimestamp && workDate <= endDateTimestamp) {
                        filteredByDate[work.id] = work;
                    }
                });
            } else filteredByDate = works;

            // Filtrar por tipo de cliente
            Object.values(filteredByDate).forEach(work => {
                const clientIds = work.clientIds;
                const hasMatchingClientType = clientIds.some(clientId => clients[clientId]?.type === type);

                if (hasMatchingClientType) {
                    filteredByClientType[work.id] = work;
                }
            });

            setFilteredWorks(filteredByClientType);
        }
    };

    /**
     * Limpia los filtros aplicados
     * @param {string} type - Tipo de filtro a limpiar ('client' o 'category')
     */
    const clear = (type) => {
        if (type === 'client') setSelectedClientType(null);
        else setSelectedCategory(null);

        let filteredByDate = {};
        const startDateTimestamp = parseDate(moment(startDate).format('DD/MM/YYYY')).getTime();;
        const endDateTimestamp = parseDate(moment(endDate).format('DD/MM/YYYY')).getTime();
    
        // Restaurar filtro por fecha si existe
        if (startDate && endDate) {
            Object.values(works).forEach(work => {
                const workDate = parseDate(work.startDate).getTime();
                if (workDate >= startDateTimestamp && workDate <= endDateTimestamp) {
                    filteredByDate[work.id] = work;
                }
            });
        } else filteredByDate = works;
        
        setFilteredWorks(filteredByDate);
    };

    /**
     * Maneja el cambio de categoría para el gráfico
     * @param {string} value - Valor de la categoría seleccionada
     */
    const handleCategoryForChart = (value) => {
        if (value !== undefined) {
            setSelectedCategoryForChart(value);
            if (startDate && endDate) {
                // Calcular datos con rango de fechas seleccionado
                const [percentageOTs, percentageInvoiced] = calculateChartData(
                    works, 
                    bids, 
                    startDate.toDate(), // Convertir moment a Date
                    endDate.toDate(),   // Convertir moment a Date
                    value
                );
                setChartData({ percentageOTs, percentageInvoiced });
            } else {
                // Calcular datos para el año actual o anterior si no hay datos
                let [percentageOTs, percentageInvoiced] = [0, 0];
                [percentageOTs, percentageInvoiced] = calculateChartData(
                    works, 
                    bids, 
                    new Date(new Date().getFullYear(), 0, 1), 
                    new Date(), 
                    value
                );
                if (percentageOTs === 0 && percentageInvoiced === 0) {
                    [percentageOTs, percentageInvoiced] = calculateChartData(
                        works, 
                        bids, 
                        new Date(new Date().getFullYear() - 1, 0, 1), 
                        new Date(), 
                        value
                    );
                }
                setChartData({ percentageOTs, percentageInvoiced });
            }
        }
    };

    /**
     * Limpia la categoría seleccionada del gráfico y recalcula los datos
     */
    const clearCategoryChart = () => {
        setSelectedCategoryForChart(null);
        if (startDate && endDate) {
            // Recalcular datos con rango de fechas seleccionado
            const [percentageOTs, percentageInvoiced] = calculateChartData(
                works, 
                bids, 
                startDate.toDate(), 
                endDate.toDate(), 
                null
            );
            setChartData({ percentageOTs, percentageInvoiced });
        } else {
            // Recalcular datos para el año actual o anterior
            let [percentageOTs, percentageInvoiced] = [0, 0];
            [percentageOTs, percentageInvoiced] = calculateChartData(
                works, 
                bids, 
                new Date(new Date().getFullYear(), 0, 1), 
                new Date(), 
                null
            );
            if (percentageOTs === 0 && percentageInvoiced === 0) {
                [percentageOTs, percentageInvoiced] = calculateChartData(
                    works, 
                    bids, 
                    new Date(new Date().getFullYear() - 1, 0, 1), 
                    new Date(), 
                    null
                );
            }
            setChartData({ percentageOTs, percentageInvoiced });
        }
    }

    return (
        loading ?
            <LoadingPage />
            :
            <Grid className="worksEmployeeCont">
                <Row center="xs">
                    <Col xs={12} md={6}>
                        <h2>Resumen de trabajos</h2>
                    </Col>
                </Row>
                <Row center="xs">
                    <Col>
                        <DatePicker 
                            placeholder="Fecha Inicio"
                            onChange={date => {
                                if (date) {
                                    const startOfMonth = date.startOf('month');
                                    setStartDate(startOfMonth);
                                } else {
                                    setStartDate(null);
                                }
                            }}
                            value={startDate}
                            format={'MM/YYYY'}
                            picker="month"
                            allowClear
                        />
                    </Col>
                    <Col style={{ paddingLeft: "10px" }}>
                        <DatePicker 
                            placeholder="Fecha Fin"
                            onChange={date => {
                                if (date) {
                                    const endOfMonth = date.endOf('month');
                                    setEndDate(endOfMonth);
                                } else {
                                    setEndDate(null);
                                }
                            }}
                            value={endDate}
                            format={'MM/YYYY'}
                            picker="month"
                            allowClear
                        />
                    </Col>
                    <Col style={{ paddingLeft: "20px" }}>
                        <Button onClick={() => getDataByDate()}>Buscar</Button>
                    </Col>
                </Row>
                <Tabs defaultActiveKey="1" centered style={{ marginTop: "2%" }}>
                    <TabPane tab="Resumen general Cot | OT" key="1">
                        <Row center="xs" style={{ marginTop: "20px" }}>
                            <Col xs={12} s={8} md={4} style={{ marginTop: '35px' }}>
                                <Pie data={data} options={options} />
                            </Col>
                            <Col xs={12} s={8} md={4}>
                                <Select
                                    placeholder="Seleccionar categoría"
                                    style={{ width: "100%" }}
                                    onChange={handleCategoryForChart}
                                    value={selectedCategoryForChart}
                                    allowClear
                                    onClear={() => {clearCategoryChart()}}
                                >
                                    {
                                        resumeType === 'SD' ?
                                        Object.keys(Constants.categorySDBidOrderMap).map((key, index) => (
                                            <Option key={key} value={Constants.categorySDBidOrderMap[key]}>{Constants.categorySDBidOrderMap[key]}</Option>
                                        )) :
                                        Object.keys(Constants.categoryARCASABidOrderMap).map((key, index) => (
                                            <Option key={key} value={Constants.categoryARCASABidOrderMap[key]}>{Constants.categoryARCASABidOrderMap[key]}</Option>
                                        ))
                                    }
                                </Select>
                                <Pie data={invoicedData} options={options1} />
                            </Col>
                        </Row>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "3%" }}>
                            <div style={{ textAlign: "center" }}>
                                <p style={{ marginBottom: "5px", fontSize: "16px", color: "#4A4A4A" }}>
                                    <strong>Cantidad de Ordenes de Trabajo con Facturacion Monotributo:</strong> {monotributoCount}
                                </p>
                                <p style={{ fontSize: "16px", color: "#4A4A4A" }}>
                                    <strong>Cantidad de Ordenes de Trabajo con Facturacion Empresa:</strong> {empresaCount}
                                </p>
                            </div>
                        </div>
                    </TabPane>
                    <TabPane tab="Categorías" key="2">
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <table style={{ borderCollapse: "collapse", width:"100%", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Categorías</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Porcentaje</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.keys(resumeType === 'SD' ? Constants.categorySDBidOrderMap : Constants.categoryARCASABidOrderMap).map((key, index) => {
                                            const categoryMap = resumeType === 'SD' ? Constants.categorySDBidOrderMap : Constants.categoryARCASABidOrderMap;
                                            const categoryKey = categoryMap[key];
                                            return (
                                                <tr key={key} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                                                    <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{key}</td>
                                                    <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{categoryPercentages[categoryKey] || '0.00'}%</strong></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tab="Localidades" key="3">
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <table style={{ borderCollapse: "collapse", width:"100%", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Localidad</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Cantidad de contrataciones por localidad</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {generateTableRows(cityCounts)}
                                    </tbody>
                                </table>
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tab="Datos por clientes" key="4">
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Monto cotizado por tipo de cliente</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={8} style={{ display: 'flex', justifyContent: 'center' }}>
                                <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "600px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Tipo de facturación</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Monto facturado</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Incidencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>Empresa</td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>USD {billingData.company.amount.toFixed(2)}</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{billingData.company.incidencia}%</strong></td>
                                        </tr>
                                        <tr style={{ backgroundColor: 'white' }}>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>Monotributo</td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>USD {billingData.monotributo.amount.toFixed(2)}</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{billingData.monotributo.incidencia}%</strong></td>
                                        </tr>
                                        <tr style={{ backgroundColor: '#e0e0e0' }}>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}><strong>Total</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>USD {(billingData.company.amount + billingData.monotributo.amount).toFixed(2)}</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Col>
                            <Col xs={24} md={4} style={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: '100%', maxWidth: '300px' }}>
                                    <Pie data={billingChartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: {label: function(tooltipItem) {
                                            const dataset = tooltipItem.dataset;
                                            const currentValue = dataset.data[tooltipItem.dataIndex];
                                            return `${currentValue}%`;
                                        }
                                    }} } }} />
                                </div>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px", paddingTop: "5%" }}>Monto contratado por tipo de cliente</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={8} style={{ display: 'flex', justifyContent: 'center' }}>
                                <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "600px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Tipo de facturación</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Monto facturado</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Incidencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "center" }}>Empresa</td>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "right" }}><strong>USD {billingDataWithWorkOrderId.company.amount.toFixed(2)}</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "right" }}><strong>{billingDataWithWorkOrderId.company.incidencia}%</strong></td>
                                        </tr>
                                        <tr style={{ backgroundColor: 'white' }}>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "center" }}>Monotributo</td>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "right" }}><strong>USD {billingDataWithWorkOrderId.monotributo.amount.toFixed(2)}</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "right" }}><strong>{billingDataWithWorkOrderId.monotributo.incidencia}%</strong></td>
                                        </tr>
                                        <tr style={{ backgroundColor: '#e0e0e0' }}>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "center" }}><strong>Total</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "right" }}><strong>USD {(billingDataWithWorkOrderId.company.amount + billingDataWithWorkOrderId.monotributo.amount).toFixed(2)}</strong></td>
                                            <td style={{ border: "1px solid #000", padding: "2px", textAlign: "right" }}></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Col>
                            <Col xs={24} md={4} style={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: '100%', maxWidth: '300px' }}>
                                    <Pie data={billingChartDataWithWorkOrderId} options={{ responsive: true, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: {label: function(tooltipItem) {
                                            const dataset = tooltipItem.dataset;
                                            const currentValue = dataset.data[tooltipItem.dataIndex];
                                            return `${currentValue}%`;
                                        }
                                    }} } }} />
                                </div>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginTop: "5%" }}>
                            <Col xs={24} md={12}>
                                <table style={{ borderCollapse: "collapse", width:"100%", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #DDD", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Cliente</th>
                                            <th style={{ border: "1px solid #DDD", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Cantidad de contrataciones</th>
                                            <th style={{ border: "1px solid #DDD", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Fecha de última contratación</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {renderClientData(clientData)}
                                    </tbody>
                                </table>
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tab="Datos de Seguimiento" key="5">
                        <Row justify="center" align="middle" style={{ marginBottom: "20px" }}>
                            <Col xs={24} md={6}>
                                <Select
                                    placeholder="Seleccionar tipo de cliente"
                                    style={{ width: "100%" }}
                                    onChange={handleClientTypeChange}
                                    value={selectedClientType}
                                    allowClear
                                    onClear={() => {clear('client')}}
                                >
                                    <Option value="privado">Privado</Option>
                                    <Option value="publico">Público</Option>
                                </Select>
                            </Col>
                            <Col xs={24} md={6} style={{ paddingLeft: "10px" }}>
                                <Select
                                    placeholder="Seleccionar rubro"
                                    style={{ width: "100%" }}
                                    onChange={handleCategoryChange}
                                    value={selectedCategory}
                                    allowClear
                                    onClear={() => {clear('category')}}
                                >
                                    {
                                        resumeType === 'SD' ?
                                        Object.keys(Constants.categorySDBidOrderMap).map((key, index) => (
                                            <Option key={key} value={Constants.categorySDBidOrderMap[key]}>{Constants.categorySDBidOrderMap[key]}</Option>
                                        )) :
                                        Object.keys(Constants.categoryARCASABidOrderMap).map((key, index) => (
                                            <Option key={key} value={Constants.categoryARCASABidOrderMap[key]}>{Constants.categoryARCASABidOrderMap[key]}</Option>
                                        ))
                                    }
                                </Select>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <table style={{ borderCollapse: "collapse", width:"100%", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>OT</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Kilometros realizados</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Kilometros promedios</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Días de Ingeniería Realizados</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Días de Ingeniería Cotizados</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Facturacion</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Tiempo de finalización</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {renderTrackingData(filteredWorks,bids, trips)}
                                    </tbody>
                                </table>
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tab="Vol. y Efic. de Contratación" key="6">
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Por rubro</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={5}>
                                <table style={{ borderCollapse: "collapse", width: "100%", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Sector</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Cotizaciones</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Contratadas</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Eficiencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.keys(categoryBidsCounts).map((category, index) => {
                                            const totalBids = categoryBidsCounts[category];
                                            const contractedBids = Object.values(bids).filter(bid => {
                                                const bidDate = parseDate(bid.startDate).getTime();
                                                const startDateTimestamp = startDate ? new Date(startDate).getTime() : 0;
                                                const endDateTimestamp = endDate ? new Date(endDate).getTime() : Number.MAX_SAFE_INTEGER;
                                                
                                                return bid.category === category && 
                                                        hasWorksForBid(bid, works) && 
                                                        bidDate >= startDateTimestamp && 
                                                        bidDate <= endDateTimestamp;
                                            }).length;
                                            const efficiency = totalBids > 0 ? ((contractedBids / totalBids) * 100).toFixed(2) : 0;
                                            return ( 
                                                <tr key={category} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                                                    <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{category}</td>
                                                    <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{totalBids}</td>
                                                    <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{contractedBids}</td>
                                                    <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{efficiency}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Col>
                            <Col xs={24} md={7} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Bar data={barChartEficientlyData} options={barChartEficientlyOptions} />
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginTop: "20px" }}>
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Por tiempo</h3>
                            </Col>
                        </Row>
                        <Row center="xs">
                            <Col xs={24} md={6}>
                                <Select
                                    defaultValue="anual"
                                    style={{ width: "100%" }}
                                    onChange={value => setTimePeriod(value) && setSelectedYearForVEOfBids(null)}
                                >
                                    <Option value="anual">Anual</Option>
                                    <Option value="mensual">Mensual</Option>
                                </Select>
                            </Col>
                            {timePeriod === 'mensual' && (
                                <Col xs={12} md={6} style={{ paddingLeft: "10px" }}>
                                    <Select
                                        placeholder="Seleccionar año"
                                        style={{ width: "100%" }}
                                        onChange={value => setSelectedYearForVEOfBids(value)}
                                    >
                                        {Object.values(bids).map(bid => bid.year).filter((year, index, self) => self.indexOf(year) === index).map(year => (
                                            <Option key={year} value={year}>{year}</Option>
                                        ))}
                                    </Select>
                                </Col>
                            )}
                        </Row>
                        <Row center="xs" style={{ marginTop: "20px" }}>
                            <Col xs={24} md={12}>
                                <Bar data={barChartData} options={{ responsive: true, plugins: { legend:{ position:'bottom'}} }} />
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tab="Distribución de Cotizaciones" key="7">
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Por cliente</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={8} style={{ display: 'flex', justifyContent: 'center' }}>
                                <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "500px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Tipo de cliente</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333" }}>Cantidad de cotizaciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>Privado</td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{privateClientCount}</strong></td>
                                        </tr>
                                        <tr style={{ backgroundColor: 'white' }}>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>Público</td>
                                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{publicClientCount}</strong></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Col>
                            <Col xs={24} md={4} style={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: '100%', maxWidth: '300px' }}>
                                    <Pie data={clientDistributionChartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: {label: function(tooltipItem) {
                                            const dataset = tooltipItem.dataset;
                                            const currentValue = dataset.data[tooltipItem.dataIndex];
                                            return `${currentValue}%`;
                                        }
                                    }} } }} />
                                </div>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px", paddingTop: "4%" }}>Por rubro</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={8} style={{ display: 'flex', justifyContent: 'center' }}>
                                <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "500px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Sector</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Cotizaciones</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Incidencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.keys(categoryBidsCounts).map((key, index) => (
                                            <tr key={key} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{key}</td>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}><strong>{categoryBidsCounts[key]}</strong></td>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}><strong>{categoryBidsPercentages[key]}%</strong></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Col>
                            <Col xs={24} md={4} style={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: '100%', maxWidth: '500px' }}>
                                    <Pie data={categoryBidsChartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: {label: function(tooltipItem) {
                                            const dataset = tooltipItem.dataset;
                                            const currentValue = dataset.data[tooltipItem.dataIndex];
                                            const label = tooltipItem.label;
                                            return `${label}: ${currentValue}%`;
                                        }
                                    }} } }} />
                                </div>
                            </Col>
                        </Row>
                    </TabPane>
                    <TabPane tab="Facturación" key="8">
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Monto cotizado anual</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <Bar data={annualBillingChartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: function(tooltipItem) { const dataset = tooltipItem.dataset; const currentValue = dataset.data[tooltipItem.dataIndex]; return `USD ${currentValue.toFixed(2)}`; } } } } }} />
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginTop: "40px" }}>
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Monto cotizado por rubro anual</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginBottom: "40px", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Col xs={24} md={6}>
                                <Select
                                    defaultValue={new Date().getFullYear()}
                                    style={{ width: "100%" }}
                                    onChange={value => setSelectedYear(value)}
                                >
                                    {Object.values(bids).map(bid => bid.year).filter((year, index, self) => self.indexOf(year) === index).map(year => (
                                        <Option key={year} value={year}>{year}</Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={12} md={6} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "600px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Sector</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Monto en dólares cotizado</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Incidencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.keys(categoryBillingData).map((category, index) => (
                                            <tr key={category} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{category}</td>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>USD {categoryBillingData[category].amount.toFixed(2)}</strong></td>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{categoryBillingData[category].incidencia}%</strong></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Col>
                            <Col xs={12} md={6} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <div style={{ width: '100%', maxWidth: '800px', height: '400px' }}>
                                    <Bar data={categoryBillingChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: function(tooltipItem) { const dataset = tooltipItem.dataset; const currentValue = dataset.data[tooltipItem.dataIndex]; return `USD ${currentValue.toFixed(2)}`; } } } } }} />
                                </div>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginTop: "40px" }}>
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Monto contratado anual</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12}>
                                <Bar data={annualContractedBillingChartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: function(tooltipItem) { const dataset = tooltipItem.dataset; const currentValue = dataset.data[tooltipItem.dataIndex]; return `USD ${currentValue.toFixed(2)}`; } } } } }} />
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginTop: "40px" }}>
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Monto contratado por rubro anual</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginBottom: "40px", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Col xs={24} md={6}>
                                <Select
                                    defaultValue={new Date().getFullYear()}
                                    style={{ width: "100%" }}
                                    onChange={value => setSelectedYearForOTAnual(value)}
                                >
                                    {
                                        Object.values(bids)
                                        .filter(bid => hasWorksForBid(bid, works))
                                        .map(bid => bid.year)
                                        .filter((year, index, self) => self.indexOf(year) === index)
                                        .map(year => (
                                            <Option key={year} value={year}>{year}</Option>
                                        ))
                                    }
                                </Select>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={12} md={6} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "600px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Sector</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Monto en dólares contratado</th>
                                            <th style={{ border: "1px solid #000", padding: "8px", backgroundColor: "#F9F9F9", color: "#333", textAlign: "center" }}>Incidencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.keys(categoryContractedBillingData).map((category, index) => (
                                            <tr key={category} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>{category}</td>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>USD {categoryContractedBillingData[category].amount.toFixed(2)}</strong></td>
                                                <td style={{ border: "1px solid #000", padding: "6px", textAlign: "right" }}><strong>{categoryContractedBillingData[category].incidencia}%</strong></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Col>
                            <Col xs={12} md={6} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <div style={{ width: '100%', maxWidth: '800px', height: '400px' }}>
                                    <Bar data={categoryContractedBillingChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: function(tooltipItem) { const dataset = tooltipItem.dataset; const currentValue = dataset.data[tooltipItem.dataIndex]; return `USD ${currentValue.toFixed(2)}`; } } } } }} />
                                </div>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle" style={{ marginTop: "40px" }}>
                            <Col xs={24} md={12}>
                                <h3 style={{ textAlign: 'center', paddingBottom: "20px" }}>Monto contratado promedio</h3>
                            </Col>
                        </Row>
                        <Row justify="center" align="middle">
                            <Col xs={24} md={12} style={{ textAlign: 'center' }}>
                                <p><strong>Monto total contratado:</strong> USD {totalContractedAmount.toFixed(2)}</p>
                                <p><strong>Cantidad total de OTs:</strong> {totalOTs}</p>
                                <p><strong>Monto promedio por OT: </strong>Monto total contratado / Cantidad total de OTs: <strong>{(totalContractedAmount / totalOTs).toFixed(2)} USD/OT </strong></p>
                            </Col>
                        </Row>
                    </TabPane>
                </Tabs>
            </Grid>
    )
};

const mapDispatchToPropsActions = dispatch => ({})

const mapStateToProps = state => ({
    user: getUser(state),
})

export default withRouter(connect(mapStateToProps, mapDispatchToPropsActions)(WorksEmployeeResume));